import { useContext } from 'react'
import { AuthContext } from '@/auth/context'

/**
 * Whether this session is a **read-only** support session — SUP-40, and SF-0's
 * NC-12.
 *
 * Screen 08 draws a warning in the tenant body reading *"Actions are disabled in
 * read-only impersonation — approving, publishing, settings blocked"*, and NC-12
 * ruled that the interface should reflect the grant's scope rather than let a
 * staff member press a control and read a refusal: the operator sees the boundary
 * before pressing it, and the server enforces it.
 *
 * **This is not the control, and must never be described as one.** NC-12's
 * security half is the important sentence in this file. The boundary is
 * `app/staff/policy.refuse`, which runs in `authenticated_user` before any route
 * does and refuses every unsafe method on a read-only grant — a rule about the
 * HTTP method rather than a list of endpoints, so a route added next year
 * inherits it. What this hook does is *describe* that boundary in the interface.
 * If the buttons were the only thing stopping a write, disabling them would be
 * the security model, and it is not: they can be re-enabled from a console in
 * ten seconds and the request still fails.
 *
 * **The flag is server-issued.** `impersonation_mode` comes off `SessionOut`,
 * which reads `sessions.impersonation_mode` — a column written by
 * `create_impersonated_session` when the exchange token was redeemed. It is not
 * read from a query parameter, a header, or anything else the page could set,
 * which is the condition NC-12 attaches: derived from the support session's own
 * claims or it means nothing.
 *
 * `false` for every ordinary session and for a **write** grant, which is one
 * comparison. A write grant is deliberately not treated as read-only: it exists
 * precisely so a second staff member's approval can buy fifteen minutes of
 * mutation, and disabling the controls through it would make that approval
 * purchase nothing.
 */
export function useSupportReadOnly(): boolean {
  // `useContext` rather than `useAuth`, which throws outside `<AuthProvider>`.
  // `Button` calls this on every render, and a `<Button>` rendered on its own in
  // a unit test has no provider around it — a shared primitive that crashes when
  // mounted alone is a primitive nothing can test. Absent provider means no
  // support session, which is the same answer as an ordinary one.
  return useContext(AuthContext)?.session?.impersonation_mode === 'read_only'
}
